import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useReadOnly } from "../hooks/useReadOnly";
import { usePermissions } from "../hooks/usePermissions";
import { LibroPagination, useClientPagination } from "../components/LibroPagination";

const EMPTY_FORM = {
  nombre: "",
  rut: "",
  giro: "",
  direccion: "",
  telefono: "",
  email: "",
  moneda: "CLP",
  pais: "CL",
  regimen_tributario: "",
  ppm_tasa: "",
  f29_dia: "20",
};

const REGIMENES = {
  "14D3": "Pro Pyme General (14 D N°3)",
  "14D8": "Pro Pyme Transparente (14 D N°8)",
  "14A": "Régimen General Semi Integrado (14 A)",
};

const payload = (form) => ({
  ...form,
  regimen_tributario: form.regimen_tributario || null,
  ppm_tasa: form.ppm_tasa === "" || form.ppm_tasa === null ? null : Number(form.ppm_tasa),
  f29_dia: form.f29_dia ? Number(form.f29_dia) : null,
});

export default function Empresas() {
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const { canWrite } = usePermissions();
  const canEdit =
    !readOnly &&
    canWrite("empresas") &&
    ["owner", "admin", "superadmin", "contador"].includes(user?.role);
  const canCreate = canEdit && user?.role === "owner";

  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const { refreshUser, refreshEmpresas } = useOutletContext() || {};
  const { refresh } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get("/empresas");
      setEmpresas(data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const empPage = useClientPagination(empresas, String(empresas.length));

  const openEdit = (e) => {
    setEditingId(e.id);
    setForm({
      nombre: e.nombre || "",
      rut: e.rut || "",
      giro: e.giro || "",
      direccion: e.direccion || "",
      telefono: e.telefono || "",
      email: e.email || "",
      moneda: e.moneda || "CLP",
      pais: e.pais || "CL",
      regimen_tributario: e.regimen_tributario || "",
      ppm_tasa: e.ppm_tasa ?? "",
      f29_dia: e.f29_dia ? String(e.f29_dia) : "20",
    });
    setEditOpen(true);
  };

  const afterSave = async () => {
    await load();
    await (refreshEmpresas?.() || Promise.resolve());
    await (refreshUser || refresh)?.();
  };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    try {
      await api.post("/empresas", payload(form));
      toast.success("Empresa creada");
      setOpen(false);
      setForm(EMPTY_FORM);
      await afterSave();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const submitEdit = async (ev) => {
    ev.preventDefault();
    try {
      await api.put(`/empresas/${editingId}`, form);
      toast.success("Empresa actualizada");
      setEditOpen(false);
      setEditingId(null);
      await afterSave();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  const formFields = (prefix) => (
    <>
      <div>
        <label className="label-caps block mb-1">Razón social</label>
        <input
          required
          data-testid={`${prefix}-empresa-nombre`}
          value={form.nombre}
          onChange={(e) => setForm({ ...form, nombre: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm"
        />
      </div>
      <div>
        <label className="label-caps block mb-1">RUT</label>
        <input
          required
          data-testid={`${prefix}-empresa-rut`}
          value={form.rut}
          onChange={(e) => setForm({ ...form, rut: e.target.value })}
          placeholder="76.123.456-7"
          className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono"
        />
      </div>
      <div>
        <label className="label-caps block mb-1">Giro</label>
        <input
          data-testid={`${prefix}-empresa-giro`}
          value={form.giro}
          onChange={(e) => setForm({ ...form, giro: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm"
        />
      </div>
      <div>
        <label className="label-caps block mb-1">Dirección</label>
        <input
          data-testid={`${prefix}-empresa-direccion`}
          value={form.direccion}
          onChange={(e) => setForm({ ...form, direccion: e.target.value })}
          className="w-full px-3 py-2 border border-border rounded-sm text-sm"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-caps block mb-1">Teléfono</label>
          <input
            data-testid={`${prefix}-empresa-telefono`}
            value={form.telefono}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            placeholder="+56 9 ..."
          />
        </div>
        <div>
          <label className="label-caps block mb-1">Email</label>
          <input
            type="email"
            data-testid={`${prefix}-empresa-email`}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-3 py-2 border border-border rounded-sm text-sm"
          />
        </div>
      </div>
    </>
  );

  return (
    <Page>
      <PageHeader
        title="Empresas"
        subtitle={
          readOnly
            ? "Empresas de ejemplo en la demostración"
            : canEdit
              ? "Edite los datos de cada RUT; los cambios se aplican de inmediato"
              : "RUT / entidades contables a las que tiene acceso"
        }
        testid="empresas-header"
        actions={
          canCreate ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="new-empresa-button"
                  className="inline-flex items-center gap-1.5 bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm"
                >
                  <Plus className="h-4 w-4" /> Nueva empresa
                </button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nueva empresa</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitCreate} className="space-y-3" data-testid="new-empresa-form">
                  {formFields("new")}
                  <DialogFooter>
                    <button
                      type="submit"
                      data-testid="submit-empresa"
                      className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm"
                    >
                      Crear empresa
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : empresas.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">No tienes empresas aún.</div>
        ) : (
          <>
            <table className="acc-table" data-testid="empresas-table">
              <thead>
                <tr>
                  <th>Razón social</th>
                  <th style={{ width: 160 }}>RUT</th>
                  <th>Giro</th>
                  <th>Dirección</th>
                  <th style={{ width: 120 }}>Teléfono</th>
                  <th style={{ width: 80 }}>Moneda</th>
                  <th style={{ width: 110 }}>Creada</th>
                  {canEdit && <th style={{ width: 72 }} />}
                </tr>
              </thead>
              <tbody>
                {empPage.pageItems.map((e) => (
                  <tr key={e.id} data-testid={`empresa-row-${e.id}`}>
                    <td className="font-medium">{e.nombre}</td>
                    <td className="font-mono">{e.rut}</td>
                    <td>{e.giro || "—"}</td>
                    <td>{e.direccion || "—"}</td>
                    <td className="text-[12px]">
                      {e.regimen_tributario ? (
                        REGIMENES[e.regimen_tributario].split(" (")[0]
                      ) : (
                        <span className="text-warning-foreground">Sin configurar</span>
                      )}
                    </td>
                    <td>{e.telefono || "—"}</td>
                    <td>{e.moneda}</td>
                    <td>{fmtDate(e.created_at)}</td>
                    {canEdit && (
                      <td>
                        <button
                          type="button"
                          onClick={() => openEdit(e)}
                          data-testid={`edit-empresa-${e.id}`}
                          className="text-primary hover:bg-primary/10 p-1.5 rounded-sm"
                          title="Editar empresa"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <LibroPagination
              pagination={empPage.pagination}
              pageSize={empPage.pageSize}
              onPageSizeChange={empPage.setPageSize}
              onPageChange={empPage.setPage}
              testidPrefix="empresas"
            />
          </>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={(v) => { if (!v) { setEditOpen(false); setEditingId(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar empresa</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3" data-testid="edit-empresa-form">
            {formFields("edit")}
            <DialogFooter>
              <button
                type="submit"
                data-testid="submit-edit-empresa"
                className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm"
              >
                Guardar cambios
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
